/**
 * Reusable Industries module — shows a grid of industry cards with images
 * Used on: Homepage, SEO Agency page, other SEO service pages
 * Design: image cards matching Indexed dark theme with cyan accents
 */

import { useState } from "react";
import { INDUSTRIES } from "@/lib/industryData";
import MobileSlider from "@/components/MobileSlider";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

// Featured industries to highlight (subset for homepage/service pages)
const FEATURED_SLUGS = [
  "seo-for-law-firms",
  "seo-for-saas",
  "seo-for-ecommerce",
  "seo-for-healthcare",
  "seo-for-financial-services",
  "seo-for-real-estate",
  "seo-for-construction-companies",
  "seo-for-restaurants",
];

interface IndustriesModuleProps {
  /** Override the heading text */
  heading?: React.ReactNode;
  /** Show all industries or just the featured subset */
  showAll?: boolean;
  /** Whether to use mobile layout */
  isMobile?: boolean;
}

export default function IndustriesModule({
  heading,
  showAll = false,
  isMobile = false,
}: IndustriesModuleProps) {
  const [hovIdx, setHovIdx] = useState<number | null>(null);

  const industries = showAll
    ? INDUSTRIES
    : INDUSTRIES.filter((ind) => FEATURED_SLUGS.includes(ind.slug));

  const displayIndustries = industries.length > 0 ? industries : INDUSTRIES;

  const defaultHeading = (
    <>
      SEO expertise across{" "}
      <span style={{ color: "#57d7e2" }}>every industry.</span>
    </>
  );

  const renderCard = (ind: typeof displayIndustries[0], idx: number) => (
    <a
      key={ind.slug}
      href={`/industries/${ind.slug}`}
      onMouseEnter={() => setHovIdx(idx)}
      onMouseLeave={() => setHovIdx(null)}
      style={{
        background: hovIdx === idx ? "rgba(87,215,226,0.04)" : "#111111",
        border: `1px solid ${hovIdx === idx ? "rgba(87,215,226,0.3)" : "rgba(255,255,255,0.07)"}`,
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        transition: "border-color 0.2s, background 0.2s, transform 0.2s",
        transform: hovIdx === idx ? "translateY(-3px)" : "translateY(0)",
        height: "100%",
        boxSizing: "border-box" as const,
      }}
    >
      {/* Image */}
      <div style={{
        width: "100%",
        aspectRatio: "16 / 10",
        overflow: "hidden",
        background: "#1a1a1a",
      }}>
        <img
          src={ind.image}
          alt={ind.name}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.4s ease",
            transform: hovIdx === idx ? "scale(1.05)" : "scale(1)",
          }}
        />
      </div>

      {/* Content */}
      <div style={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
        <h3
          style={{
            color: "white",
            fontSize: isMobile ? "16px" : "17px",
            fontWeight: 600,
            fontFamily: ff,
            margin: 0,
            lineHeight: 1.3,
          }}
        >
          {ind.name}
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: "#57d7e2",
            fontSize: "13px",
            fontWeight: 600,
            fontFamily: ff,
            marginTop: "auto",
          }}
        >
          Learn more
          <span
            style={{
              display: "inline-block",
              transition: "transform 0.2s",
              transform: hovIdx === idx ? "translateX(4px)" : "translateX(0)",
            }}
          >
            →
          </span>
        </div>
      </div>
    </a>
  );

  return (
    <section
      className="section-grid"
      style={{
        background: "#0a0a0a",
        padding: isMobile ? "60px 20px" : "100px 72px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        {/* Header row: H2 left, See all right */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px", marginBottom: "56px" }}>
          <h2
            style={{
              color: "white",
              fontSize: isMobile ? "32px" : "48px",
              fontWeight: 700,
              fontFamily: ff,
              margin: 0,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              textAlign: "left",
            }}
          >
            {heading || defaultHeading}
          </h2>
          {!showAll && (
            <a href="/industries"
              style={{ color: "#57d7e2", fontSize: "16px", fontWeight: 600, fontFamily: ff, textDecoration: "none", transition: "opacity 0.2s", flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
              See all →
            </a>
          )}
        </div>

        {/* Industry cards — slider on mobile, grid on desktop */}
        {isMobile ? (
          <MobileSlider itemWidth={240} gap={14} padding={20}>
            {displayIndustries.map((ind, idx) => renderCard(ind, idx))}
          </MobileSlider>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: displayIndustries.length <= 4
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)",
              gap: "16px",
            }}
          >
            {displayIndustries.map((ind, idx) => renderCard(ind, idx))}
          </div>
        )}
      </div>
    </section>
  );
}
